import Controller, { inject as controller } from '@ember/controller';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { showModal } from 'sh-www/utils/modal-controls';

const CLASS_NAME = 'SH-c-credentials';

class PasswordResetController extends Controller {
  @controller application;
  @service auth;
  @service session;
  @service shData;
  @service ('commands/user-commands') userCommands;
  @service('state-managers.application') applicationStateManager;
  @service('state-manager-hybrids.heartbeat-state-manager-hybrid') heartbeatStateManager;
  @service('error-tracker') errorTracker;

  @tracked oauthProviders = null;
  @tracked samlProviders = null;
  @tracked errors = {};

  get className() {
    return CLASS_NAME;
  }

  get email() {
    return this.session.currentUser.email;
  }

  @task
  *loadUserOAuthProviders() {
    const response = yield this.userCommands.getUserOAuthProviders(this.session.currentUser);

    this.shData.pushResponseToStore(response);
    this.oauthProviders = this.shData.getModelArrayFromResponse(response, 'OAuthProvider');
  }

  @task
  *loadUserSamlProviders() {
    const response = yield this.userCommands.getUserSamlProviders(this.session.currentUser);

    this.shData.pushResponseToStore(response);
    this.samlProviders = this.shData.getModelArrayFromResponse(response, 'SamlProvider');
  }

  @task
  *resetPassword(newPassword, newPasswordConfirmed, oldPassword = null) {
    const email = this.email;

    try {
      yield this.heartbeatStateManager.pauseHeartbeat(true);
      yield this.auth.updatePassword(email, newPassword, newPasswordConfirmed, oldPassword);
      this.applicationStateManager.addToast('Your password has been updated');
    } finally {
      yield this.heartbeatStateManager.pauseHeartbeat(false);
    }
  }

  @task
  *onGoogleSSO(credential) {
    try {
      yield this.userCommands.addGoogleJwt(this.session.currentUser, credential);

      yield this.loadUserOAuthProviders.perform();
    } catch (e) {
      this.errorTracker.warn('Google authentication failed', this, e);
      handleErrorResponseWithModal(this, e);
      throw e;
    }
  }

  @task
  *onRemoveOAuthProvider(oauthProvider) {
    if (!this.session.currentUser.hasPassword) {
      showModal(this, 'alert', {
        bodyText: 'You must set a password before unlinking your SSO account.',
        titleText: 'Error',
        actionButtonText: 'OK',
      });
      return;
    }

    try {
      yield this.userCommands.removeOauthProvider(oauthProvider);

      yield this.loadUserOAuthProviders.perform();
    } catch (e) {
      this.errorTracker.warn('Google authentication failed', this, e);
      handleErrorResponseWithModal(this, e);
      throw e;
    }
  }
}

export default PasswordResetController;
